import React, { useMemo, useState, useEffect, useCallback } from "react";
import {
  FaWarehouse,
  FaBuilding,
  FaCogs,
  FaUser,
  FaIndustry,
  FaArrowRight,
  FaMapMarkedAlt,
  FaFlagCheckered,
  FaUserAlt,
} from "react-icons/fa";
import useAuth from "../../Hooks/useAuth";
import { displayLogText } from "../../utils/deviceActivityLabels";
import { useGetDeviceMovementHistoryQuery } from "../../store/storageRoles/devicesSharedApi";
import { useLazySearchUsersQuery } from "../../store/storageRoles/usersApi";
import {
  EmptyState,
  ListView,
  ListToolbar,
  ListSkeleton,
} from "../shared";
import { CustomDropdown, DateRangeField, Pagination } from "../atoms";

// Duruma göre renk/ikon — DeviceCard konum renkleriyle uyumlu.
const STATUS_STYLE = {
  Depo: {
    icon: FaWarehouse,
    chip: "bg-purple-50 text-purple-700 border-purple-200",
  },
  Firma: {
    icon: FaBuilding,
    chip: "bg-indigo-50 text-indigo-700 border-indigo-200",
  },
  Servis: { icon: FaCogs, chip: "bg-teal-50 text-teal-700 border-teal-200" },
  Plasiyer: { icon: FaUser, chip: "bg-blue-50 text-blue-700 border-blue-200" },
  Üretim: {
    icon: FaIndustry,
    chip: "bg-green-50 text-green-700 border-green-200",
  },
};
const FALLBACK = {
  icon: FaMapMarkedAlt,
  chip: "bg-gray-50 text-gray-600 border-gray-200",
};
const styleFor = (s) => STATUS_STYLE[s] || FALLBACK;

const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("tr-TR", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "-";
const fmtTime = (d) =>
  d
    ? new Date(d).toLocaleTimeString("tr-TR", {
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";

const PAGE_SIZE = 25;

const StatusChip = ({ status }) => {
  const st = styleFor(status);
  const Icon = st.icon;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md border text-xs font-semibold whitespace-nowrap ${st.chip}`}
    >
      <Icon className="w-3 h-3 shrink-0" />
      {status || "-"}
    </span>
  );
};

const MovementCells = {
  dateTime: (row) => (
    <span className="inline-flex flex-col leading-tight">
      <span className="text-sm font-semibold text-gray-800">
        {fmtDate(row.date)}
      </span>
      <span className="text-xs text-gray-500">{fmtTime(row.date)}</span>
    </span>
  ),
  movement: (row) => (
    <span className="inline-flex items-center gap-1.5 flex-wrap">
      <StatusChip status={row.valueFrom} />
      <FaArrowRight className="w-3 h-3 text-gray-300 shrink-0" />
      <StatusChip status={row.valueTo} />
    </span>
  ),
  operator: (row) => (
    <span className="inline-flex items-center gap-1.5 text-sm text-gray-700">
      <FaUserAlt className="w-3 h-3 text-gray-400 shrink-0" />
      {row.operatorName || "-"}
    </span>
  ),
  description: (row) => (
    <span className="text-sm text-gray-600 line-clamp-2 block" title={displayLogText(row.description)}>
      {displayLogText(row.description) || "-"}
    </span>
  ),
  note: (row) => (
    <span className="text-sm text-gray-600 line-clamp-2 block" title={displayLogText(row.note)}>
      {displayLogText(row.note) || "-"}
    </span>
  ),
};

/**
 * Cihazın firma/depo/servis arasındaki hareket geçmişi.
 *
 * Liste deseni proje kuralı gereği ListToolbar + ListView kompozisyonudur
 * (Kullanıcılar sekmesiyle aynı iskelet). Satırlar sabit genişlik taşımaz,
 * tabloya yayılır.
 *
 * Filtreleme ve sayfalama CLIENT tarafındadır: hareket geçmişi tek istekte
 * gelen küçük bir liste, her filtre için sunucuya gitmek gereksiz olurdu.
 */
export default function DeviceMovementTable({
  deviceId,
  token: tokenProp,
}) {
  const { auth } = useAuth();
  const token = tokenProp || auth?.accessToken;
  // Hareket sekmesi yalnız depo panelinde — bayide YOK (#302).
  const { data, isLoading, isFetching, isError } = useGetDeviceMovementHistoryQuery(
    { id: deviceId, token },
    { skip: !deviceId || !token }
  );

  // Açılışta son 1 ay: hareket geçmişi uzun olabiliyor, ilk ekranda en
  // güncel dönem gösterilir. Kullanıcı aralığı genişletebilir/temizleyebilir.
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d;
  });
  const [endDate, setEndDate] = useState(() => new Date());
  const [movementOpt, setMovementOpt] = useState(null);
  const [operatorOpt, setOperatorOpt] = useState(null);
  const [page, setPage] = useState(1);

  const movements = useMemo(() => data?.data || [], [data]);

  // API kronolojik döner; log olduğu için en yeni üstte gösterilir.
  const allRows = useMemo(
    () => [...movements].reverse().map((m, i) => ({ ...m, _key: `mv-${i}` })),
    [movements]
  );

  // --- Hareket filtresi: veride GERÇEKTEN bulunan geçişler -----------------
  // Sabit 5x5 kombinasyon listesi üretmek yanıltıcı olurdu; hiç yaşanmamış
  // geçişi seçilebilir yapmak boş tablo demektir.
  const movementOptions = useMemo(() => {
    const seen = new Map();
    allRows.forEach((m) => {
      const key = `${m.valueFrom || "-"}→${m.valueTo || "-"}`;
      if (!seen.has(key)) {
        seen.set(key, {
          id: key,
          label: `${m.valueFrom || "-"} → ${m.valueTo || "-"}`,
          value: key,
        });
      }
    });
    return [...seen.values()];
  }, [allRows]);

  const [movementQuery, setMovementQuery] = useState("");
  const filteredMovementOptions = useMemo(() => {
    const q = movementQuery.trim().toLowerCase();
    if (!q) return movementOptions;
    return movementOptions.filter((o) => o.label.toLowerCase().includes(q));
  }, [movementOptions, movementQuery]);

  // --- İşlemi yapan: tüm kullanıcılarda debounce'lu arama ------------------
  // NOT: searchUsers yetkiye göre süzemiyor (yalnızca role/subRole). Bu yüzden
  // listede bu cihaza hiç dokunmamış kullanıcılar da çıkabilir; öyle biri
  // seçilirse tablo boş gelir. Sunucuya yetki filtresi eklenirse burası daralır.
  const [triggerSearchUsers, { data: userData, isFetching: usersLoading }] =
    useLazySearchUsersQuery();

  const handleUserSearch = useCallback(
    (term) => {
      if (!token) return;
      triggerSearchUsers({ token, search: term || "", page: 1, limit: 20 });
    },
    [token, triggerSearchUsers]
  );

  const operatorOptions = useMemo(() => {
    const list = userData?.data?.users || userData?.data || [];
    if (!Array.isArray(list)) return [];
    return list.map((u) => {
      const full = [u.name, u.lastName].filter(Boolean).join(" ") || u.email;
      return { id: u._id || full, label: full, value: full, description: u.email };
    });
  }, [userData]);

  // --- Filtre uygulaması ---------------------------------------------------
  const rows = useMemo(() => {
    const startMs = startDate ? new Date(startDate).setHours(0, 0, 0, 0) : null;
    // Bitiş günü DAHİL olmalı — gün sonuna kadar genişletilir.
    const endMs = endDate ? new Date(endDate).setHours(23, 59, 59, 999) : null;
    const op = operatorOpt?.value?.toLowerCase();

    return allRows.filter((m) => {
      if (startMs || endMs) {
        const t = m.date ? new Date(m.date).getTime() : null;
        if (t === null || Number.isNaN(t)) return false;
        if (startMs && t < startMs) return false;
        if (endMs && t > endMs) return false;
      }
      if (movementOpt?.value) {
        const key = `${m.valueFrom || "-"}→${m.valueTo || "-"}`;
        if (key !== movementOpt.value) return false;
      }
      if (op && !(m.operatorName || "").toLowerCase().includes(op)) return false;
      return true;
    });
  }, [allRows, startDate, endDate, movementOpt, operatorOpt]);

  // Filtre değişince ilk sayfaya dön — aksi halde boş sayfada kalınır.
  useEffect(() => {
    setPage(1);
  }, [startDate, endDate, movementOpt, operatorOpt]);

  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const pageRows = useMemo(
    () => rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [rows, page]
  );

  const originStatus = movements.length > 0 ? movements[0].valueFrom : null;
  const loading = isLoading || isFetching;
  // Varsayılan tarih aralığı hep dolu olduğu için "filtre var mı" sorusu
  // boş-durum metnini belirlemeye yetmez; hiç kayıt olmaması ile seçilen
  // aralıkta kayıt olmaması ayrı şeylerdir.
  const hasAnyMovement = allRows.length > 0;

  const columns = [
    {
      key: "dateTime",
      label: "Tarih / Saat",
      cellClassName: "px-4 py-3 whitespace-nowrap",
      render: MovementCells.dateTime,
    },
    {
      key: "movement",
      label: "Hareket",
      cellClassName: "px-4 py-3",
      render: MovementCells.movement,
    },
    {
      key: "operator",
      label: "İşlemi Yapan",
      cellClassName: "px-4 py-3",
      render: MovementCells.operator,
    },
    {
      key: "description",
      label: "İşlem Detayı",
      cellClassName: "px-4 py-3 align-top",
      render: MovementCells.description,
    },
    {
      key: "note",
      label: "Not",
      cellClassName: "px-4 py-3 align-top",
      render: MovementCells.note,
    },
  ];

  const renderMobileCard = (row) => (
    <div
      key={row._key}
      className="bg-white border border-gray-200 rounded-xl p-3 space-y-2"
    >
      <div className="flex items-center justify-between gap-2">
        {MovementCells.movement(row)}
      </div>
      <div className="flex items-center justify-between gap-2 text-xs text-gray-500">
        <span>
          {fmtDate(row.date)} {fmtTime(row.date)}
        </span>
        <span className="truncate">{row.operatorName || "-"}</span>
      </div>
      {row.description && (
        <p className="text-xs text-gray-600 line-clamp-2">{displayLogText(row.description)}</p>
      )}
      {row.note && (
        <p className="text-xs text-gray-500 line-clamp-2">Not: {displayLogText(row.note)}</p>
      )}
    </div>
  );

  return (
    <div className="flex flex-col h-full w-full">
      <ListToolbar
        filters={
          <div className="flex flex-wrap items-end gap-3">
            <DateRangeField
              startDate={startDate}
              setStartDate={setStartDate}
              endDate={endDate}
              setEndDate={setEndDate}
              size="sm"
              // disablePortal YOK: popper toolbar içinde kalırsa ListView'in
              // sticky thead'i (z-30) takvimin üstüne biniyor. Portal ile
              // body'ye taşınınca zIndex 9999 devreye girer.
              className="flex gap-3 [&>div]:w-40"
            />
            <CustomDropdown
              label="Hareket"
              value={movementOpt}
              onChange={(o) => setMovementOpt(o?.id === "all" ? null : o)}
              options={filteredMovementOptions}
              allOption={{ id: "all", label: "Tüm Hareketler" }}
              placeholder="Tüm Hareketler"
              onSearch={setMovementQuery}
              searchPlaceholder="Hareket ara..."
              emptyMessage="Eşleşen hareket yok"
              compact
              className="w-full sm:w-56"
            />
            <CustomDropdown
              label="İşlemi Yapan"
              value={operatorOpt}
              onChange={(o) => setOperatorOpt(o?.id === "all" ? null : o)}
              options={operatorOptions}
              allOption={{ id: "all", label: "Herkes" }}
              placeholder="Herkes"
              onSearch={handleUserSearch}
              loading={usersLoading}
              searchPlaceholder="Kullanıcı ara..."
              emptyMessage="Kullanıcı bulunamadı"
              compact
              className="w-full sm:w-60"
            />
          </div>
        }
      />

      <ListView
        headers={columns.map((c) => ({ label: c.label }))}
        renderRow={(row) => (
          <tr className="bg-white hover:bg-gray-50 transition-colors">
            {columns.map((c) => (
              <td key={c.key} className={c.cellClassName}>
                {c.render(row)}
              </td>
            ))}
          </tr>
        )}
        data={pageRows}
        rowKey="_key"
        renderMobileCard={renderMobileCard}
        isLoading={loading}
        loadingNode={
          <ListSkeleton columns={columns} message="Hareket geçmişi yükleniyor..." />
        }
        empty={
          isError ? (
            <EmptyState
              message="Hareket geçmişi yüklenemedi"
              suggestion="Bağlantıyı kontrol edip tekrar deneyin."
            />
          ) : (
            <EmptyState
              message={
                hasAnyMovement
                  ? "Seçilen aralıkta hareket yok"
                  : "Hareket kaydı yok"
              }
              suggestion={
                hasAnyMovement
                  ? "Tarih aralığını genişletmeyi veya filtreleri değiştirmeyi deneyin."
                  : "Bu cihaz için henüz bir hareket kaydedilmemiş."
              }
            />
          )
        }
        footer={
          <div className="flex flex-col gap-2">
            {originStatus && (
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <FaFlagCheckered className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                <span>Başlangıç:</span>
                <StatusChip status={originStatus} />
              </div>
            )}
            {totalPages > 1 && (
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                onPageChange={setPage}
                itemsPerPage={PAGE_SIZE}
                totalItems={rows.length}
              />
            )}
          </div>
        }
      />

    </div>
  );
}
